import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Image, Type, Wand2, Copy, Download, RefreshCw, ChevronDown, 
  Zap, Check, Settings, History, Star, Bookmark, Share2, MoreHorizontal,
  FileText, Mic, Globe, Hash
} from 'lucide-react';

export default function ContentPreview({ customization }) {
  const theme = customization?.theme || { primary: '#8b5cf6', secondary: '#d946ef', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)' };
  const companyName = customization?.companyName || 'ContentAI';
  
  const [activeTab, setActiveTab] = useState('text');
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [selectedTone, setSelectedTone] = useState('Professional');
  const [wordCount, setWordCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const sampleOutput = `# Introducing the Future of Productivity

Transform how your team works with our **AI-powered platform** that combines intelligent automation with seamless collaboration.

## Why Teams Choose ${companyName}

🚀 **50% Faster Delivery**
Ship projects in half the time with AI-assisted workflows that eliminate repetitive tasks.

🤝 **Seamless Collaboration**  
Real-time co-editing, smart notifications, and AI meeting summaries keep everyone aligned.

📊 **Data-Driven Insights**
Make confident decisions with predictive analytics and AI-powered recommendations.

---

*Ready to transform your workflow?* **Start your free trial today.**`;

  const templates = [
    { label: 'Marketing Copy', icon: '📢', color: '#ef4444' },
    { label: 'Blog Post', icon: '📝', color: theme.primary },
    { label: 'Social Media', icon: '📱', color: '#ec4899' },
    { label: 'Product Desc', icon: '📦', color: '#f59e0b' },
  ];

  const handleGenerate = () => {
    if (!prompt.trim()) return;
    setIsGenerating(true);
    setShowResult(false);
    
    setTimeout(() => {
      setIsGenerating(false);
      setGeneratedContent(sampleOutput);
      setShowResult(true);
      setWordCount(sampleOutput.split(/\s+/).length);
    }, 2500);
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ 
      height: '100%', 
      background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        background: 'white',
        borderBottom: '1px solid #e5e5e5',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ 
            width: '36px', height: '36px', 
            background: theme.gradient,
            borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 15px ${theme.primary}40`
          }}>
            <Sparkles size={18} color="white" />
          </div>
          <div>
            <span style={{ fontWeight: '700', fontSize: '14px', color: '#18181b' }}>{companyName}</span>
            <div style={{ fontSize: '10px', color: '#71717a' }}>Powered by AI</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '10px', padding: '6px 10px', 
            background: `linear-gradient(135deg, ${theme.primary}20, ${theme.primary}10)`, 
            borderRadius: '8px', color: theme.primary, fontWeight: '500'
          }}>
            <Zap size={12} />
            1,847 credits
          </div>
          <History size={18} color="#71717a" style={{ cursor: 'pointer' }} />
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>
        {/* Tab Switcher */}
        <div style={{ 
          display: 'flex', gap: '6px', marginBottom: '14px',
          background: '#e5e5e5', padding: '4px', borderRadius: '12px'
        }}>
          {[
            { id: 'text', label: 'Text', icon: Type },
            { id: 'image', label: 'Image', icon: Image },
            { id: 'audio', label: 'Voice', icon: Mic },
          ].map(tab => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                flex: 1, padding: '10px',
                background: activeTab === tab.id ? 'white' : 'transparent',
                border: 'none', borderRadius: '10px',
                fontSize: '12px', fontWeight: activeTab === tab.id ? '600' : '400',
                color: activeTab === tab.id ? theme.primary : '#71717a',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                boxShadow: activeTab === tab.id ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <tab.icon size={14} />
              {tab.label}
            </motion.button>
          ))}
        </div>

        {/* Quick Templates */}
        <div style={{ marginBottom: '14px' }}>
          <p style={{ fontSize: '11px', color: '#71717a', marginBottom: '8px', fontWeight: '500' }}>
            Quick templates
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {templates.map((t) => (
              <motion.button
                key={t.label}
                onClick={() => setPrompt(`Write a compelling ${t.label.toLowerCase()} for ${companyName} that builds AI solutions`)}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '10px 14px',
                  background: 'white',
                  border: '1px solid #e5e5e5',
                  borderRadius: '10px',
                  fontSize: '11px',
                  color: '#3f3f46',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <span style={{ fontSize: '14px' }}>{t.icon}</span>
                {t.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Input Area */}
        <div style={{
          background: 'white',
          borderRadius: '14px',
          padding: '14px',
          border: '1px solid #e5e5e5',
          marginBottom: '14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the content you want to generate... Be specific for better results!"
            style={{
              width: '100%',
              height: '80px',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontSize: '13px',
              fontFamily: 'inherit',
              color: '#18181b',
              lineHeight: 1.6
            }}
          />
          <div style={{ 
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
            marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f4f4f5'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', background: '#f4f4f5',
                borderRadius: '8px', fontSize: '11px', color: '#52525b', cursor: 'pointer'
              }}>
                <Globe size={12} />
                {selectedTone}
                <ChevronDown size={10} />
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', background: '#f4f4f5',
                borderRadius: '8px', fontSize: '11px', color: '#52525b', cursor: 'pointer'
              }}>
                <Hash size={12} />
                ~500 words
              </div>
            </div>
            <motion.button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              whileHover={!isGenerating && prompt.trim() ? { scale: 1.02 } : {}}
              whileTap={!isGenerating && prompt.trim() ? { scale: 0.98 } : {}}
              style={{
                padding: '10px 20px',
                background: isGenerating ? '#a1a1aa' : theme.gradient,
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: isGenerating ? 'none' : `0 4px 15px ${theme.primary}40`
              }}
            >
              {isGenerating ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <RefreshCw size={14} />
                  </motion.div>
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 size={14} />
                  Generate
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Output Area */}
        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                background: 'white',
                borderRadius: '14px',
                border: '1px solid #e5e5e5',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            >
              {/* Output Header */}
              <div style={{ 
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 14px',
                borderBottom: '1px solid #f4f4f5',
                background: `linear-gradient(90deg, ${theme.primary}08, ${theme.secondary || theme.primary}05)`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <Sparkles size={16} color={theme.primary} />
                  </motion.div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#18181b' }}>
                    Generated Content
                  </span>
                  <span style={{ 
                    fontSize: '9px', padding: '3px 8px', 
                    background: '#f4f4f5', borderRadius: '6px', color: '#71717a'
                  }}>
                    {wordCount} words
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[
                    { icon: copied ? Check : Copy, label: copied ? 'Copied!' : 'Copy', onClick: handleCopy, active: copied },
                    { icon: Download, label: 'Export' },
                    { icon: Bookmark, label: 'Save' },
                    { icon: RefreshCw, label: 'Regenerate' },
                  ].map(action => (
                    <motion.button
                      key={action.label}
                      onClick={action.onClick}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '6px 10px',
                        background: action.active ? `${theme.primary}20` : '#f4f4f5',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '10px',
                        color: action.active ? theme.primary : '#52525b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <action.icon size={12} />
                      {action.label}
                    </motion.button>
                  ))}
                </div>
              </div>
              
              {/* Output Content */}
              <div style={{ 
                padding: '16px',
                fontSize: '12px', 
                lineHeight: 1.8, 
                color: '#3f3f46',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {generatedContent.split('\n').map((line, i) => {
                  if (line.startsWith('# ')) {
                    return <h1 key={i} style={{ fontSize: '16px', fontWeight: '700', color: '#18181b', margin: '0 0 12px' }}>{line.slice(2)}</h1>;
                  }
                  if (line.startsWith('## ')) {
                    return <h2 key={i} style={{ fontSize: '14px', fontWeight: '600', color: '#18181b', margin: '16px 0 8px' }}>{line.slice(3)}</h2>;
                  }
                  if (line.startsWith('---')) {
                    return <hr key={i} style={{ border: 'none', borderTop: '1px solid #e5e5e5', margin: '16px 0' }} />;
                  }
                  if (line.trim() === '') {
                    return <br key={i} />;
                  }
                  return (
                    <p key={i} style={{ margin: '8px 0' }} dangerouslySetInnerHTML={{
                      __html: line
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                    }} />
                  );
                })}
              </div>
              
              {/* Quality Score */}
              <div style={{
                padding: '12px 14px',
                borderTop: '1px solid #f4f4f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#fafafa'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Star size={12} color="#f59e0b" fill="#f59e0b" />
                    <span style={{ fontSize: '10px', color: '#71717a' }}>Quality: </span>
                    <span style={{ fontSize: '10px', fontWeight: '600', color: theme.primary }}>Excellent</span>
                  </div>
                  <div style={{ width: '1px', height: '12px', background: '#e5e5e5' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#71717a' }}>Readability: </span>
                    <span style={{ fontSize: '10px', fontWeight: '600', color: '#18181b' }}>Grade 8</span>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: '6px 12px',
                    background: theme.gradient,
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Share2 size={10} />
                  Use Content
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
